import _ from "lodash";
import { createSelector } from "reselect";
import { getUserSelectedAccountCode } from "../../app/appSelectors.js";
import Asset from "../Asset/Asset.js";
import { LITE_TRADE_ORDER_STATUSES } from "./liteTradeConstants";

const moduleSelector = state => state.liteTrade;

export const getLiteTradeConfig = createSelector(
    moduleSelector, state => state.config,
);
export const getLiteTradeInitError = createSelector(
    moduleSelector, state => state.initError,
);
export const getLiteTradeInitLoading = createSelector(
    moduleSelector, state => state.isInitLoading,
);
export const getLiteTradeSession = createSelector(
    moduleSelector, state => state.session,
);
export const getLiteTradeSessionToken = createSelector(
    getLiteTradeSession, state => state?.sessionToken,
);
export const getLiteTradeIsLoggedIn = createSelector(
    getLiteTradeSession, state => !!state?.sessionToken,
);
export const getLiteTradeIsPingLoading = createSelector(
    moduleSelector, state => state.isPingLoading,
);
export const getLiteTradePingError = createSelector(
    moduleSelector, state => state.pingError,
);
export const getLiteTradeIsLoginLoading = createSelector(
    moduleSelector, state => state.isLoginLoading,
);
export const getLiteTradeLoginError = createSelector(
    moduleSelector, state => state.loginError,
);

export const getLiteTradeAllAccountsMetrics = createSelector(
    moduleSelector, state => state.metrics,
);

export const getLiteTradeAccountMetrics = createSelector(
    [moduleSelector, getUserSelectedAccountCode], (state, account) => state.metrics[account],
);

export const makeSelectorGetLiteTradeAccountMetrics = (account) => createSelector(
    moduleSelector, state => state.metrics[account],
);

export const getLiteTradeAccountPortfolio = createSelector(
    [moduleSelector, getUserSelectedAccountCode], (state, account) => state.portfolios[account] || {},
);

export const getLiteTradeAccountOrders = createSelector(
    getLiteTradeAccountPortfolio, state => state.orders || [],
);

export const getLiteTradeAccountPositions = createSelector(
    getLiteTradeAccountPortfolio, state => state.positions || [],
);

export const makeSelectorGetLiteTradeAccountPositionInfo = (positionCode) => createSelector(
    getLiteTradeAccountPortfolio, state => state.positions?.find(item => item.positionCode === positionCode),
);

export const makeSelectorGetLiteTradeAccountOrderInfo = (orderId) => createSelector(
    getLiteTradeAccountPortfolio, state => state.orders?.find(item => item.orderId.toString() === orderId),
);

export const makeSelectorGetLiteTradeAccountOrderProtectionOrderList = (orderId) => createSelector(
    [makeSelectorGetLiteTradeAccountOrderInfo(orderId), getLiteTradeAccountOrders,], (orderInfo, orders) =>
        orders.filter(order =>
            orderInfo?.links?.find(link => link?.linkedClientOrderId === order?.clientOrderId)
        ),
);

export const makeSelectorGetLiteTradeAccountOrderProtectionOrderInfoByType = (orderId, protectionOrderTypeToSearch) => createSelector(
    makeSelectorGetLiteTradeAccountOrderProtectionOrderList(orderId), protectionOrders =>
        protectionOrders.find(protectionOrder => protectionOrder?.type === protectionOrderTypeToSearch)
);

export const makeSelectorGetLiteTradeAccountPositionProtectionOrderList = (positionCode) => createSelector(
    [getLiteTradeAccountOrders], (orders) =>
        orders.filter(order =>
            order.status === LITE_TRADE_ORDER_STATUSES.WORKING && order.legs[0].positionCode === positionCode,
        ),
);

export const makeSelectorGetLiteTradeAccountPositionProtectionOrderInfoByType = (positionCode, protectionOrderTypeToSearch) => createSelector(
    makeSelectorGetLiteTradeAccountPositionProtectionOrderList(positionCode), (protectionOrders) =>
        protectionOrders.find(protectionOrder => protectionOrder.type === protectionOrderTypeToSearch)
);

export const makeSelectorGetLiteTradeAccountOrdersByAccountCode = (accountCode) => createSelector(
    moduleSelector, state => state.portfolios[accountCode]?.orders,
);

export const makeSelectorGetLiteTradeAccountOrderInfoByAccountCode = (orderId, accountCode) => createSelector(
    moduleSelector, state => state.portfolios[accountCode]?.orders?.find(item => item.orderId.toString() === orderId),
);

export const getLiteTradeAssets = createSelector(
    moduleSelector, state => state.assets || [],
);

export const getLiteTradeAssetsBySymbol = createSelector(
    getLiteTradeAssets, assets => _.keyBy(assets, 'symbol')
);

export const makeSelectorGetLiteTradeAssetInfo = (symbol) => createSelector(
    moduleSelector, state => state.assets.find(item => item.symbol === symbol),
);

export const makeSelectorGetAssetInstance = (symbol) => createSelector(
    moduleSelector, state => {
        const assetParams = state.assets.find(item => item.symbol === symbol);
        return new Asset(assetParams);
    },
);

export const getLiteTradeAssetsTypes = createSelector(
    getLiteTradeAssets, state => _.chain(state)
        .map('type')
        .uniq()
        .sort()
        .value()
);

export const getAuthVerifyInProgress = createSelector(
    moduleSelector, state => state.authVerifyInProgress
);

export const getLiteTradeMarketData = createSelector(
    moduleSelector, state => state.marketData,
);

export const makeSelectLiteTradeMarketDataBySymbol = (symbol) => createSelector(
    getLiteTradeMarketData, (marketData) => marketData[symbol]
);

export const getLiteTradeMarketDataCandle = createSelector(
    moduleSelector, state => state.marketDataCandle,
);

export const makeSelectLiteTradeMarketDataCandleBySymbol = (symbol) => createSelector(
    getLiteTradeMarketDataCandle, (marketDataCandle) => marketDataCandle[symbol]
);

export const getMarketDataWatchers = createSelector(
    moduleSelector, state => state.marketDataWatchers,
);

export const getMarketDataCandleWatchers = createSelector(
    moduleSelector, state => state.marketDataCandleWatchers,
);

export const getAccountStatPL = createSelector(
    moduleSelector, state => state.stat_pl,
);
